import { makeAutoObservable } from "mobx";
import { makeStoreProvider } from "@synthesis/shared-utils";
import { z } from "zod";
import type {
  CreateRequestBodyAdjoiningTypeEnumKey,
  CreateRequestBodyApplicantTypeEnumKey,
  CreateRequestBodyPlannedWorksTypeEnumKey,
  CreateRequestBodyRailwayEnumKey,
  CreateRequestBodyRequestSubjectEnumKey,
  CreateRequestBodyTrackFundingEnumKey,
  CreateRequestBodyTypeEnumKey,
  RequestDetailResponse,
  UpdateDraftData,
} from "@synthesis/shared-services/external/application/types";

import type { ApplicationSelectOption } from "../../constants";
import { isEditableExternalApplicationStatus } from "../../constants";
import { createApplicationApplicantFieldLengths } from "./create-application-applicant-input-masks";

// ---------------------------------- TYPES ----------------------------------

export type CreateApplicationStepKey = "type" | "object-data" | "documents" | "applicant";

export type CreateApplicationCategory = "adjacent" | "crossing";

export type CreateApplicationStep = {
  key: CreateApplicationStepKey;
  label: string;
};

export type CreateApplicationDraftMeta = {
  category: CreateApplicationCategory;
  categoryLabel: string;
  draftTitle?: string;
  applicantData?: ApplicantDataFormValues;
  documents?: CreateApplicationDraftDocumentMeta[];
};

export type CreateApplicationSelectOption = ApplicationSelectOption;

export type ApplicantType = CreateRequestBodyApplicantTypeEnumKey;

export type SignerType = "applicant" | "representative";

export type AdjacentPlannedWorkType = CreateRequestBodyPlannedWorksTypeEnumKey;

export type AdjacentConnectionType = CreateRequestBodyAdjoiningTypeEnumKey;

export type AdjacentFundingSource = CreateRequestBodyTrackFundingEnumKey;

export type AdjacentLandDataAvailability = "known" | "unknown";

export type AdjacentApplicantCargoSender = "yes" | "no";

export type AdjacentObjectDataFormValues = {
  projectName: string;
  plannedWorkType: string;
  connectionType: string;
  trackPurpose: string;
  fundingSource: string;
  fundingSourceOther: string;
  pathLengthMeters: string;
  projectDescription: string;
  cadastralDataAvailability: AdjacentLandDataAvailability;
  landStatus: string;
  landLocationDescription: string;
  cadastralNumbers: string[];
  licensedSubsoilDevelopment: boolean;
  subsoilLicenseNumberAndDate: string;
  subsoilLicenseValidity: string;
  hasDesignDocumentationExpertise: boolean;
  designDocumentationExpertiseDetails: string;
  railway: string;
  stationSection: string;
  junctionLocation: string;
  coordinates: string;
  nearestExistingTrack: string;
  connectionPlace: string;
  connectionPlaceComment: string;
  applicantIsCargoSender: AdjacentApplicantCargoSender;
  guaranteeLettersRequired: boolean;
  implementationStartDate: string;
  implementationEndDate: string;
  commissioningDate: string;
};

export type CrossingIntersectionFormValues = {
  stationSection: string;
  junctionLocation: string;
  coordinates: string;
  comment: string;
};

export type CrossingCommunicationFormValues = {
  utilityStructureType: string;
  utilityPurpose: string;
  plannedWorkType: string;
  crossingTechnology: string;
  technicalCharacteristics: string;
  intersections: CrossingIntersectionFormValues[];
};

export type CrossingObjectDataFormValues = {
  projectName: string;
  railway: string;
  communications: CrossingCommunicationFormValues[];
  implementationStartDate: string;
  implementationEndDate: string;
  commissioningDate: string;
};

export type CreateApplicationDocumentsContext = {
  applicantType?: ApplicantType;
  signerType?: SignerType;
  plannedWorkType?: string;
  connectionType?: string;
  guaranteeLettersRequired?: boolean;
  hasDesignDocumentationExpertise?: boolean;
};

export type CreateApplicationDocumentDefinition = {
  id: string;
  order: number;
  title: string;
  required: boolean;
  hint?: string;
  isVisible?: (context: CreateApplicationDocumentsContext) => boolean;
};

export type CreateApplicationDocumentUploadedFile = {
  key: string;
  fileName: string;
};

export type CreateApplicationDocumentTableRow = CreateApplicationDocumentDefinition & {
  uploadedFiles: CreateApplicationDocumentUploadedFile[];
  showValidationError?: boolean;
};

export type CreateApplicationDocumentUpload = {
  localId: string;
  file: File;
  fileName: string;
};

export type CreateApplicationDocumentServerFile = {
  fileId: string;
  fileName: string;
};

export type CreateApplicationDraftDocumentMeta = CreateApplicationDocumentDefinition & {
  uploadedFileNames?: string[];
  /** Legacy single-file drafts before multi-upload. */
  uploadedFileName?: string;
};

export type CreateApplicationDocumentUploadHandlers = {
  onUploadClick: (documentId: string) => void;
  onRemoveFile: (documentId: string, fileKey: string) => void;
};

export const createCreateApplicationDocumentUploadLocalId = () => crypto.randomUUID();

export type ApplicantDataFormValues = {
  applicantType: ApplicantType;
  organizationSearch: string;
  organization: string;
  shortOrganizationName: string;
  snils: string;
  okved: string;
  oktmoOkato: string;
  legalAddress: string;
  actualAddress: string;
  actualAddressMatchesLegal: boolean;
  postalAddressMatchesRegistration: boolean;
  phones: string[];
  emails: string[];
  useSeparateContractEmail: boolean;
  contractEmails: string[];
  postalAddress: string;
  inn: string;
  kpp: string;
  okpo: string;
  ogrn: string;
  bankName: string;
  bik: string;
  settlementAccount: string;
  correspondentAccount: string;
  signerType: SignerType;
  signerFullName: string;
  signerPosition: string;
  signerAuthority: string;
  signerAuthorityDetails: string;
  paymentByAnotherOrganization: boolean;
  payerApplicantType: Exclude<ApplicantType, "INDIVIDUAL">;
  payerOrganization: string;
  payerShortOrganizationName: string;
  payerInn: string;
  payerKpp: string;
  payerOgrn: string;
  payerOkved: string;
  payerOktmoOkato: string;
  payerOkpo: string;
  payerLegalAddress: string;
  payerActualAddress: string;
  payerActualAddressMatchesLegal: boolean;
  payerPostalAddress: string;
  payerPostalAddressMatchesRegistration: boolean;
  payerPhones: string[];
  payerEmails: string[];
  payerUseSeparateContractEmail: boolean;
  payerContractEmails: string[];
  payerPaymentContactFullName: string;
  payerPaymentContactPosition: string;
  payerPaymentContactPhones: string[];
  payerPaymentContactEmails: string[];
  payerBankName: string;
  payerBik: string;
  payerSettlementAccount: string;
  payerCorrespondentAccount: string;
};

// ---------------------------------- CONSTANTS ----------------------------------

export const CREATE_APPLICATION_STEPS: CreateApplicationStep[] = [
  { key: "type", label: "Тип заявки" },
  { key: "applicant", label: "О заявителе" },
  { key: "object-data", label: "Данные объекта" },
  { key: "documents", label: "Документы" },
];

export const CREATE_APPLICATION_STEP_TITLES: Record<CreateApplicationStepKey, string> = {
  type: "Выберите тип заявки",
  "object-data": "Заполните информацию об объекте",
  documents: "Приложите документы к заявке",
  applicant: "Заполните данные организации",
};

export const CREATE_APPLICATION_TYPE_OPTIONS = [
  {
    value: "adjacent",
    title: "Примыкание",
    description: "Организация примыкания объекта к инфраструктуре железной дороги, включая подъездные пути и технологические соединения",
  },
  {
    value: "crossing",
    title: "Пересечение",
    description: "Размещение коммуникаций, автомобильных дорог или иных объектов с пересечением полосы отвода железной дороги",
  },
] as const;

/** Тип заявки по умолчанию на первом шаге мастера (согласовано в BUG-3344). */
export const DEFAULT_CREATE_APPLICATION_CATEGORY: CreateApplicationCategory = "adjacent";

export const isIndividualApplicantType = (applicantType: ApplicantType) => applicantType === "INDIVIDUAL";

export const isLegalEntityApplicantType = (applicantType: ApplicantType) => applicantType === "LEGAL_ENTITY";

export const ADJACENT_LAND_STATUS_OPTIONS: CreateApplicationSelectOption[] = [
  { value: "ownership", label: "Собственность заявителя" },
  { value: "lease", label: "Аренда" },
  { value: "planned-acquisition", label: "Планируется оформление прав" },
  { value: "other", label: "Иное" },
];

export const ADJACENT_LAND_DATA_AVAILABILITY_OPTIONS: Array<{ value: AdjacentLandDataAvailability; label: string }> = [
  { value: "unknown", label: "Нет, кадастровый номер неизвестен" },
  { value: "known", label: "Да, известен кадастровый номер или квартал" },
];

export const ADJACENT_CARGO_SENDER_OPTIONS: Array<{
  value: AdjacentApplicantCargoSender;
  label: string;
}> = [
  { value: "yes", label: "Да" },
  { value: "no", label: "Нет" },
];

export const isAdjacentLandSectionRequired = (plannedWorkType: string) =>
  plannedWorkType === "NEW_TRACK_CONSTRUCTION" ||
  plannedWorkType === "DEVELOPMENT" ||
  plannedWorkType === "new-track-construction" ||
  plannedWorkType === "existing-adjacent-development";

/**
 * Кадастровый номер / квартал: `77:01:0001010` (квартал) или `77:01:0001010:123` (участок,
 * допускаются дополнительные сегменты для составных объектов).
 */
const CADASTRAL_NUMBER_PATTERN = /^\d{2}:\d{2}:\d{7}(:\d+)*$/;

export const isValidCadastralNumber = (value: string): boolean => CADASTRAL_NUMBER_PATTERN.test(value);

export const isAdjacentConstructionWorkType = (plannedWorkType?: string) =>
  plannedWorkType === "NEW_TRACK_CONSTRUCTION" || plannedWorkType === "new-track-construction";

export const isAdjacentDevelopmentWorkType = (plannedWorkType?: string) =>
  plannedWorkType === "DEVELOPMENT" || plannedWorkType === "existing-adjacent-development";

export const isAdjacentConstructionOrDevelopmentWorkType = (plannedWorkType?: string) =>
  isAdjacentConstructionWorkType(plannedWorkType) || isAdjacentDevelopmentWorkType(plannedWorkType);

export const isAdjacentReconstructionOrRestorationWorkType = (plannedWorkType?: string) =>
  plannedWorkType === "RECONSTRUCTION" || plannedWorkType === "RESTORATION";

/** Authority docs: If ЮЛ, or signer = representative (story: «Если ЮЛ, Представитель»). */
export const isSignatoryAuthorityDocumentVisible = (context: CreateApplicationDocumentsContext) =>
  context.applicantType === "LEGAL_ENTITY" || context.signerType === "representative";

export const SIGNER_TYPE_OPTIONS: Array<{ value: SignerType; label: string }> = [
  { value: "applicant", label: "Заявитель" },
  { value: "representative", label: "Представитель" },
];

export const SIGNER_TYPE_LABELS: Record<SignerType, string> = Object.fromEntries(
  SIGNER_TYPE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<SignerType, string>;

export const APPLICANT_TYPE_OPTIONS: Array<{ value: ApplicantType; label: string }> = [
  { value: "LEGAL_ENTITY", label: "Юридическое лицо" },
  { value: "SOLE_PROPRIETOR", label: "Индивидуальный предприниматель" },
  { value: "INDIVIDUAL", label: "Физическое лицо" },
];

export const APPLICANT_TYPE_LABELS: Record<ApplicantType, string> = Object.fromEntries(
  APPLICANT_TYPE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<ApplicantType, string>;

export const PAYER_APPLICANT_TYPE_OPTIONS = APPLICANT_TYPE_OPTIONS.filter((option) => option.value !== "INDIVIDUAL");

const SIGNATORY_AUTHORITY_HINT =
  "Приложите документы, подтверждающие право лица подписывать обращение и действовать от имени заявителя.\nНапример: устав, приказ о назначении руководителя, доверенность, выписка из ЕГРЮЛ, документ о регистрации ИП, паспорт (для физического лица).";

const createDocument = (
  id: string,
  order: number,
  title: string,
  options?: {
    required?: boolean;
    hint?: string;
    isVisible?: (context: CreateApplicationDocumentsContext) => boolean;
  },
): CreateApplicationDocumentDefinition => ({
  id,
  order,
  title,
  required: options?.required ?? false,
  ...(options?.hint ? { hint: options.hint } : {}),
  ...(options?.isVisible ? { isVisible: options.isVisible } : {}),
});

export const ADJACENT_REQUIRED_DOCUMENTS: CreateApplicationDocumentDefinition[] = [
  createDocument("adjacent-signatory-authority", 1, "Документы, подтверждающие полномочия лица, подписывающего обращение", {
    required: true,
    hint: SIGNATORY_AUTHORITY_HINT,
    isVisible: isSignatoryAuthorityDocumentVisible,
  }),
  createDocument("adjacent-ip-registration", 2, "Заверенная копия документа о государственной регистрации ИП", {
    required: true,
    hint: "Прикрепите заверенную копию свидетельства о государственной регистрации индивидуального предпринимателя или выписки из ЕГРИП, подтверждающей статус ИП.",
    isVisible: (context) => context.applicantType === "SOLE_PROPRIETOR" && isAdjacentConstructionOrDevelopmentWorkType(context.plannedWorkType),
  }),
  createDocument("adjacent-constituent-documents", 3, "Заверенные копии учредительных документов, подтверждающего внесение записи в ЕГРЮЛ", {
    required: true,
    hint: "Прикрепите заверенные копии учредительных документов, свидетельства о постановке на учет в налоговом органе и документа, подтверждающего внесение записи в ЕГРЮЛ.",
    isVisible: (context) => context.applicantType === "LEGAL_ENTITY" && isAdjacentConstructionOrDevelopmentWorkType(context.plannedWorkType),
  }),
  createDocument("adjacent-land-rights", 4, "Правоустанавливающие документы на земельные участки", {
    required: true,
    hint: "Прикрепите документы, подтверждающие право использования земельного участка (собственность, аренда или иное право).",
    isVisible: (context) => isAdjacentConstructionOrDevelopmentWorkType(context.plannedWorkType),
  }),
  createDocument("adjacent-topographic-plan", 5, "Топографический план местности с кадастровой привязкой", {
    required: true,
    hint: "Прикрепите топографический план местности с отображением предполагаемого размещения железнодорожного пути и его привязкой к существующей инфраструктуре, включая кадастровые номера земельных участков (или кадастровые кварталы при их отсутствии).\nРекомендуемый масштаб: 1:500–1:2000.",
    isVisible: (context) => isAdjacentConstructionWorkType(context.plannedWorkType),
  }),
  createDocument("adjacent-station-scheme", 6, "Схематический план станции с точкой примыкания", {
    required: true,
    hint: "Прикрепите схематический план станции с указанием предполагаемого места примыкания нового железнодорожного пути необщего пользования к инфраструктуре ОАО «РЖД» либо к железнодорожному пути другого владельца, примыкающему к инфраструктуре ОАО «РЖД»",
    isVisible: (context) => isAdjacentConstructionOrDevelopmentWorkType(context.plannedWorkType),
  }),
  createDocument("adjacent-existing-path-scheme", 7, "Схематический план существующего путевого развития (при реконструкции)", {
    required: true,
    hint: "Прикрепите схематический план существующего путевого развития железнодорожного пути необщего пользования, участвующего в проекте реконструкции или развития примыкания.",
    isVisible: (context) => isAdjacentReconstructionOrRestorationWorkType(context.plannedWorkType),
  }),
  createDocument("adjacent-project-solutions", 8, "Основные проектные решения / предпроектные проработки", {
    hint: "Прикрепите основные проектные решения или предпроектные проработки по развитию инфраструктуры ОАО «РЖД» и/или железнодорожного пути необщего пользования (при наличии).",
  }),
  createDocument("adjacent-owner-consent", 9, "Письменное согласие владельца железнодорожного пути (если примыкание через сторонний путь)", {
    required: true,
    hint: "Прикрепите письменные согласия всех владельцев железнодорожных путей, через инфраструктуру которых осуществляется примыкание (по каждому владельцу отдельно).",
    isVisible: (context) => context.connectionType === "VIA_OTHER_OWNER" && isAdjacentConstructionWorkType(context.plannedWorkType),
  }),
  createDocument("adjacent-traffic-volumes", 10, "Информация о перевозках грузов (по форме приложения №2)", {
    required: true,
    hint: "Прикрепите заполненную форму информации о существующих и планируемых объемах перевозок грузов (Приложение №2 к Правилам). Форма должна содержать данные по корреспонденциям, объемам перевозок, типам грузов, подвижному составу и видам отправки.",
    isVisible: (context) =>
      isAdjacentConstructionOrDevelopmentWorkType(context.plannedWorkType) ||
      isAdjacentReconstructionOrRestorationWorkType(context.plannedWorkType) ||
      context.guaranteeLettersRequired === true,
  }),
  createDocument("adjacent-guarantee-letters", 11, "Гарантийные письма от грузоотправителей", {
    required: true,
    hint: "Прикрепите гарантийные письма от всех грузоотправителей, подтверждающих заявленные объемы перевозок по проекту.",
    isVisible: (context) => context.guaranteeLettersRequired === true,
  }),
  createDocument("adjacent-expertise-conclusion", 12, "Положительное заключение экспертизы", {
    required: true,
    hint: "Прикрепите положительное заключение экспертизы проектной документации.",
    isVisible: (context) => context.hasDesignDocumentationExpertise === true,
  }),
  createDocument("adjacent-other-materials", 13, "Иные материалы", {
    hint: "Дополнительные документы, относящиеся к заявке и не указанные в перечне.",
  }),
];

export const CROSSING_REQUIRED_DOCUMENTS: CreateApplicationDocumentDefinition[] = [
  createDocument("crossing-signatory-authority", 1, "Документы, подтверждающие полномочия лица, подписывающего обращение", {
    required: true,
    hint: SIGNATORY_AUTHORITY_HINT,
    isVisible: isSignatoryAuthorityDocumentVisible,
  }),
  createDocument("crossing-route-scheme", 2, "Схема (план) трассы инженерной коммуникации", {
    required: true,
    hint: "Загрузите схему прохождения инженерной коммуникации с указанием предполагаемого места пересечения с железнодорожной инфраструктурой. На схеме должна быть отображена трасса коммуникации, участок не менее 50 м в обе стороны от пересечения, существующие объекты и ориентиры, а также (при наличии) охранные зоны. При наличии нескольких пересечений необходимо отобразить их все и указать расстояния между ними.\n\nФайл: PDF / JPG / PNG. DWG — при наличии (для уточнения координат и трассы).",
  }),
  createDocument("crossing-situational-plan", 3, "Ситуационный план участка пересечения", {
    required: true,
    hint: "Загрузите ситуационный план участка с указанием предполагаемого места пересечения железнодорожной инфраструктуры. План должен отображать положение объекта относительно железнодорожных путей и окружающей территории (автодороги, населённые пункты, сооружения и иные ориентиры), а также предполагаемую трассу инженерной коммуникации и место пересечения.",
  }),
  createDocument("crossing-topographic-plan", 4, "Топографический план места пересечения (масштаб 1:500–1:2000)", {
    hint: "Загрузите схему на топографической основе, показывающую предполагаемое место пересечения инженерной коммуникации с железнодорожной инфраструктурой. На плане должны быть отображены трасса коммуникации, железнодорожные пути, существующие объекты и границы полосы отвода, а также координатная привязка.\nМасштаб рекомендуется 1:500–1:2000.",
  }),
  createDocument("crossing-site-photos", 5, "Фотографии места работ (необязательно)", {
    hint: "Фотографии предполагаемого места пересечения или прилегающей территории при наличии.",
  }),
  createDocument("crossing-other-materials", 6, "Иные материалы", {
    hint: "Дополнительные документы, относящиеся к заявке и не указанные в перечне.",
  }),
];

export const CREATE_APPLICATION_DOCUMENTS_BY_CATEGORY: Record<CreateApplicationCategory, CreateApplicationDocumentDefinition[]> = {
  adjacent: ADJACENT_REQUIRED_DOCUMENTS,
  crossing: CROSSING_REQUIRED_DOCUMENTS,
};

export const toCreateApplicationDocumentMeta = ({
  id,
  order,
  title,
  required,
  hint,
}: CreateApplicationDocumentDefinition): CreateApplicationDocumentDefinition => ({
  id,
  order,
  title,
  required,
  ...(hint ? { hint } : {}),
});

const requiredString = (message: string) => z.string().trim().min(1, message);
const requiredSelect = (message: string) => z.string().min(1, message);

export const adjacentObjectDataSchema = z
  .object({
    projectName: requiredString("Укажите название"),
    plannedWorkType: requiredSelect("Выберите вид планируемых работ"),
    connectionType: requiredSelect("Выберите тип примыкания"),
    trackPurpose: z.string(),
    fundingSource: requiredSelect("Выберите источник финансирования"),
    fundingSourceOther: z.string(),
    pathLengthMeters: z.string(),
    projectDescription: z.string(),
    cadastralDataAvailability: z.enum(["known", "unknown"]),
    landStatus: z.string(),
    landLocationDescription: z.string(),
    cadastralNumbers: z.array(z.string()),
    licensedSubsoilDevelopment: z.boolean(),
    subsoilLicenseNumberAndDate: z.string(),
    subsoilLicenseValidity: z.string(),
    hasDesignDocumentationExpertise: z.boolean(),
    designDocumentationExpertiseDetails: z.string(),
    railway: requiredSelect("Выберите дорогу"),
    stationSection: z.string(),
    junctionLocation: z.string(),
    coordinates: z.string(),
    nearestExistingTrack: z.string(),
    connectionPlace: z.string(),
    connectionPlaceComment: z.string(),
    applicantIsCargoSender: z.enum(["yes", "no"]),
    guaranteeLettersRequired: z.boolean(),
    implementationStartDate: z.string(),
    implementationEndDate: z.string(),
    commissioningDate: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.licensedSubsoilDevelopment) {
      if (!data.subsoilLicenseNumberAndDate.trim()) {
        ctx.addIssue({
          code: "custom",
          message: "Укажите номер и дату лицензии",
          path: ["subsoilLicenseNumberAndDate"],
        });
      }

      if (!data.subsoilLicenseValidity.trim()) {
        ctx.addIssue({
          code: "custom",
          message: "Укажите срок действия лицензии",
          path: ["subsoilLicenseValidity"],
        });
      }
    }

    if (data.hasDesignDocumentationExpertise && !data.designDocumentationExpertiseDetails.trim()) {
      addRequiredIssue(ctx, ["designDocumentationExpertiseDetails"], "Укажите номер и дату заключения экспертизы");
    }
  });

export const crossingObjectDataSchema = z
  .object({
    projectName: requiredString("Укажите название"),
    railway: requiredSelect("Выберите дорогу"),
    communications: z.array(
      z.object({
        utilityStructureType: requiredSelect("Выберите вид инженерной коммуникации"),
        utilityPurpose: z.string(),
        plannedWorkType: requiredSelect("Выберите вид планируемых работ"),
        crossingTechnology: z.string(),
        technicalCharacteristics: z.string(),
        intersections: z.array(
          z.object({
            stationSection: z.string(),
            junctionLocation: z.string(),
            coordinates: z.string(),
            comment: z.string(),
          }),
        ),
      }),
    ),
    implementationStartDate: z.string(),
    implementationEndDate: z.string(),
    commissioningDate: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.communications.length === 0) {
      addRequiredIssue(ctx, ["communications"], "Добавьте хотя бы одну инженерную коммуникацию");
    }

    data.communications.forEach((communication, communicationIndex) => {
      if (communication.intersections.length === 0) {
        addRequiredIssue(ctx, ["communications", communicationIndex, "intersections"], "Добавьте хотя бы одно место пересечения");
      }
    });
  });

const addRequiredIssue = (ctx: z.RefinementCtx, path: (string | number)[], message: string) => {
  ctx.addIssue({
    code: "custom",
    message,
    path,
  });
};

const validateRequiredEmail = (ctx: z.RefinementCtx, path: (string | number)[], value: string | undefined, requiredMessage: string) => {
  const email = value?.trim();

  if (!email) {
    addRequiredIssue(ctx, path, requiredMessage);
    return;
  }

  validateOptionalEmail(ctx, path, email);
};

const validateOptionalEmail = (ctx: z.RefinementCtx, path: (string | number)[], value: string | undefined) => {
  const email = value?.trim();

  if (!email) {
    return;
  }

  if (!z.email().safeParse(email).success) {
    addRequiredIssue(ctx, path, "Укажите корректный e-mail");
  }
};

const DIGITS_ONLY_REGEX = /^\d+$/;

const formatDigitsLengthMessage = (lengths: readonly number[]) => {
  if (lengths.length === 1) {
    return `${lengths[0]} цифр`;
  }

  return `${lengths.slice(0, -1).join(", ")} или ${lengths.at(-1)} цифр`;
};

const validateDigitsField = (
  ctx: z.RefinementCtx,
  path: (string | number)[],
  value: string,
  allowedLengths: readonly number[],
  fieldLabel: string,
) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return;
  }

  if (!DIGITS_ONLY_REGEX.test(trimmed) || !allowedLengths.includes(trimmed.length)) {
    addRequiredIssue(ctx, path, `${fieldLabel} должен содержать ${formatDigitsLengthMessage(allowedLengths)}`);
  }
};

type ApplicantDigitsFieldValidation = {
  field: keyof ApplicantDataFormValues;
  lengths: readonly number[];
  label: string;
};

const validateApplicantDigitsFields = (ctx: z.RefinementCtx, data: ApplicantDataFormValues, fields: ApplicantDigitsFieldValidation[]) => {
  for (const { field, lengths, label } of fields) {
    const value = data[field];

    if (typeof value === "string") {
      validateDigitsField(ctx, [field], value, lengths, label);
    }
  }
};

export const applicantDataSchema = z
  .object({
    applicantType: z.enum(["LEGAL_ENTITY", "SOLE_PROPRIETOR", "INDIVIDUAL"]),
    organizationSearch: z.string(),
    organization: requiredString("Укажите организацию"),
    shortOrganizationName: z.string(),
    snils: z.string(),
    okved: z.string(),
    oktmoOkato: z.string(),
    legalAddress: z.string(),
    actualAddress: z.string(),
    actualAddressMatchesLegal: z.boolean(),
    postalAddressMatchesRegistration: z.boolean(),
    phones: z.array(z.string()),
    emails: z.array(z.string()),
    useSeparateContractEmail: z.boolean(),
    contractEmails: z.array(z.string()),
    postalAddress: requiredString("Укажите почтовый адрес"),
    inn: requiredString("Укажите ИНН"),
    kpp: z.string(),
    okpo: z.string(),
    ogrn: z.string(),
    bankName: z.string(),
    bik: z.string(),
    settlementAccount: z.string(),
    correspondentAccount: z.string(),
    signerType: z.enum(["applicant", "representative"]),
    signerFullName: z.string(),
    signerPosition: z.string(),
    signerAuthority: z.string(),
    signerAuthorityDetails: z.string(),
    paymentByAnotherOrganization: z.boolean(),
    payerApplicantType: z.enum(["LEGAL_ENTITY", "SOLE_PROPRIETOR"]),
    payerOrganization: z.string(),
    payerShortOrganizationName: z.string(),
    payerInn: z.string(),
    payerKpp: z.string(),
    payerOgrn: z.string(),
    payerOkved: z.string(),
    payerOktmoOkato: z.string(),
    payerOkpo: z.string(),
    payerLegalAddress: z.string(),
    payerActualAddress: z.string(),
    payerActualAddressMatchesLegal: z.boolean(),
    payerPostalAddress: z.string(),
    payerPostalAddressMatchesRegistration: z.boolean(),
    payerPhones: z.array(z.string()),
    payerEmails: z.array(z.string()),
    payerUseSeparateContractEmail: z.boolean(),
    payerContractEmails: z.array(z.string()),
    payerPaymentContactFullName: z.string(),
    payerPaymentContactPosition: z.string(),
    payerPaymentContactPhones: z.array(z.string()),
    payerPaymentContactEmails: z.array(z.string()),
    payerBankName: z.string(),
    payerBik: z.string(),
    payerSettlementAccount: z.string(),
    payerCorrespondentAccount: z.string(),
  })
  .superRefine((data, ctx) => {
    validateApplicantDigitsFields(ctx, data, [{ field: "inn", lengths: createApplicationApplicantFieldLengths.inn, label: "ИНН" }]);

    if (data.applicantType !== "LEGAL_ENTITY") {
      validateApplicantDigitsFields(ctx, data, [{ field: "snils", lengths: createApplicationApplicantFieldLengths.snils, label: "СНИЛС" }]);
    }

    if (!isIndividualApplicantType(data.applicantType)) {
      validateApplicantDigitsFields(ctx, data, [
        {
          field: "ogrn",
          lengths: createApplicationApplicantFieldLengths.ogrn,
          label: "ОГРН / ОГРНИП",
        },
        {
          field: "oktmoOkato",
          lengths: createApplicationApplicantFieldLengths.oktmoOkato,
          label: "ОКТМО / ОКАТО",
        },
        { field: "okpo", lengths: createApplicationApplicantFieldLengths.okpo, label: "ОКПО" },
        { field: "kpp", lengths: createApplicationApplicantFieldLengths.kpp, label: "КПП" },
        { field: "bik", lengths: createApplicationApplicantFieldLengths.bik, label: "БИК" },
        {
          field: "settlementAccount",
          lengths: createApplicationApplicantFieldLengths.settlementAccount,
          label: "Расчётный счёт",
        },
        {
          field: "correspondentAccount",
          lengths: createApplicationApplicantFieldLengths.correspondentAccount,
          label: "Корреспондентский счёт",
        },
      ]);
    }

    if (!isIndividualApplicantType(data.applicantType)) {
      const organizationRequiredFields: Array<[keyof ApplicantDataFormValues, string]> = [
        ...(isLegalEntityApplicantType(data.applicantType)
          ? ([["shortOrganizationName", "Укажите сокращённое наименование"]] as Array<[keyof ApplicantDataFormValues, string]>)
          : []),
        ["ogrn", "Укажите ОГРН / ОГРНИП"],
        ["legalAddress", "Укажите юридический адрес / адрес регистрации"],
        ["actualAddress", "Укажите фактический адрес"],
      ];

      for (const [field, message] of organizationRequiredFields) {
        const value = data[field];

        if (typeof value !== "string" || !value.trim()) {
          addRequiredIssue(ctx, [field], message);
        }
      }
    }

    if (isIndividualApplicantType(data.applicantType)) {
      if (!data.legalAddress.trim()) {
        addRequiredIssue(ctx, ["legalAddress"], "Укажите адрес регистрации");
      }
    }

    if (!data.phones[0]?.trim()) {
      addRequiredIssue(ctx, ["phones", 0], "Укажите контактный телефон");
    }

    validateRequiredEmail(
      ctx,
      ["emails", 0],
      data.emails[0],
      isIndividualApplicantType(data.applicantType) ? "Укажите e-mail" : "Укажите e-mail организации",
    );

    if (data.useSeparateContractEmail) {
      validateRequiredEmail(ctx, ["contractEmails", 0], data.contractEmails[0], "Укажите e-mail договорной документации");
    }

    if (!data.postalAddress.trim()) {
      addRequiredIssue(ctx, ["postalAddress"], "Укажите почтовый адрес");
    }

    if (!isIndividualApplicantType(data.applicantType)) {
      if (!data.signerFullName.trim()) {
        addRequiredIssue(ctx, ["signerFullName"], "Укажите ФИО подписанта");
      }

      if (!data.signerAuthority.trim()) {
        addRequiredIssue(ctx, ["signerAuthority"], "Укажите основание полномочий");
      }

      if (!data.bankName.trim()) {
        addRequiredIssue(ctx, ["bankName"], "Укажите банк");
      }

      if (!data.bik.trim()) {
        addRequiredIssue(ctx, ["bik"], "Укажите БИК");
      }

      if (!data.settlementAccount.trim()) {
        addRequiredIssue(ctx, ["settlementAccount"], "Укажите расчётный счёт");
      }

      if (!data.correspondentAccount.trim()) {
        addRequiredIssue(ctx, ["correspondentAccount"], "Укажите корреспондентский счёт");
      }
    }

    if (!isIndividualApplicantType(data.applicantType) && data.signerType === "representative") {
      if (!data.signerPosition.trim()) {
        addRequiredIssue(ctx, ["signerPosition"], "Укажите должность подписанта");
      }

      if (!data.signerAuthorityDetails.trim()) {
        addRequiredIssue(ctx, ["signerAuthorityDetails"], "Укажите реквизиты основания");
      }
    }

    if (!data.paymentByAnotherOrganization) {
      return;
    }

    validateApplicantDigitsFields(ctx, data, [
      {
        field: "payerInn",
        lengths: createApplicationApplicantFieldLengths.inn,
        label: "ИНН плательщика",
      },
      {
        field: "payerOgrn",
        lengths: createApplicationApplicantFieldLengths.ogrn,
        label: "ОГРН / ОГРНИП плательщика",
      },
      {
        field: "payerOktmoOkato",
        lengths: createApplicationApplicantFieldLengths.oktmoOkato,
        label: "ОКТМО / ОКАТО плательщика",
      },
      {
        field: "payerOkpo",
        lengths: createApplicationApplicantFieldLengths.okpo,
        label: "ОКПО плательщика",
      },
      {
        field: "payerKpp",
        lengths: createApplicationApplicantFieldLengths.kpp,
        label: "КПП плательщика",
      },
      {
        field: "payerBik",
        lengths: createApplicationApplicantFieldLengths.bik,
        label: "БИК плательщика",
      },
      {
        field: "payerSettlementAccount",
        lengths: createApplicationApplicantFieldLengths.settlementAccount,
        label: "Расчётный счёт плательщика",
      },
      {
        field: "payerCorrespondentAccount",
        lengths: createApplicationApplicantFieldLengths.correspondentAccount,
        label: "Корреспондентский счёт плательщика",
      },
    ]);

    const payerRequiredFields: Array<[keyof ApplicantDataFormValues, string]> = [
      ["payerOrganization", "Укажите организацию-плательщика"],
      ...(data.payerApplicantType === "LEGAL_ENTITY"
        ? ([["payerShortOrganizationName", "Укажите сокращённое наименование плательщика"]] as Array<[keyof ApplicantDataFormValues, string]>)
        : []),
      ["payerInn", "Укажите ИНН плательщика"],
      ["payerOgrn", "Укажите ОГРН / ОГРНИП плательщика"],
      ["payerLegalAddress", "Укажите юридический адрес / адрес регистрации плательщика"],
      ["payerActualAddress", "Укажите фактический адрес плательщика"],
      ["payerPostalAddress", "Укажите почтовый адрес плательщика"],
      ["payerPaymentContactFullName", "Укажите контактное лицо по вопросам оплаты"],
      ["payerPaymentContactPosition", "Укажите должность контактного лица"],
      ["payerBankName", "Укажите банк плательщика"],
      ["payerBik", "Укажите БИК плательщика"],
      ["payerSettlementAccount", "Укажите расчётный счёт плательщика"],
      ["payerCorrespondentAccount", "Укажите корреспондентский счёт плательщика"],
    ];

    for (const [field, message] of payerRequiredFields) {
      const value = data[field];

      if (typeof value !== "string" || !value.trim()) {
        addRequiredIssue(ctx, [field], message);
      }
    }

    if (!data.payerPhones[0]?.trim()) {
      addRequiredIssue(ctx, ["payerPhones", 0], "Укажите контактный телефон");
    }

    validateRequiredEmail(ctx, ["payerEmails", 0], data.payerEmails[0], "Укажите e-mail организации");

    validateOptionalEmail(ctx, ["payerPaymentContactEmails", 0], data.payerPaymentContactEmails[0]);

    if (data.payerUseSeparateContractEmail) {
      validateRequiredEmail(ctx, ["payerContractEmails", 0], data.payerContractEmails[0], "Укажите e-mail договорной документации плательщика");
    }
  });

export const DEFAULT_ADJACENT_OBJECT_DATA: AdjacentObjectDataFormValues = {
  projectName: "",
  plannedWorkType: "",
  connectionType: "",
  trackPurpose: "",
  fundingSource: "",
  fundingSourceOther: "",
  pathLengthMeters: "",
  projectDescription: "",
  cadastralDataAvailability: "unknown",
  landStatus: "",
  landLocationDescription: "",
  cadastralNumbers: [],
  licensedSubsoilDevelopment: false,
  subsoilLicenseNumberAndDate: "",
  subsoilLicenseValidity: "",
  hasDesignDocumentationExpertise: false,
  designDocumentationExpertiseDetails: "",
  railway: "",
  stationSection: "",
  junctionLocation: "",
  coordinates: "",
  nearestExistingTrack: "",
  connectionPlace: "",
  connectionPlaceComment: "",
  applicantIsCargoSender: "yes",
  guaranteeLettersRequired: false,
  implementationStartDate: "",
  implementationEndDate: "",
  commissioningDate: "",
};

export const DEFAULT_CROSSING_INTERSECTION: CrossingIntersectionFormValues = {
  stationSection: "",
  junctionLocation: "",
  coordinates: "",
  comment: "",
};

export const createDefaultCrossingIntersection = (): CrossingIntersectionFormValues => ({
  ...DEFAULT_CROSSING_INTERSECTION,
});

export const createDefaultCrossingCommunication = (): CrossingCommunicationFormValues => ({
  utilityStructureType: "",
  utilityPurpose: "",
  plannedWorkType: "",
  crossingTechnology: "",
  technicalCharacteristics: "",
  intersections: [createDefaultCrossingIntersection()],
});

export const createDefaultCrossingObjectData = (): CrossingObjectDataFormValues => ({
  projectName: "",
  railway: "",
  communications: [createDefaultCrossingCommunication()],
  implementationStartDate: "",
  implementationEndDate: "",
  commissioningDate: "",
});

export const DEFAULT_CROSSING_OBJECT_DATA: CrossingObjectDataFormValues = createDefaultCrossingObjectData();

export const DEFAULT_APPLICANT_DATA: ApplicantDataFormValues = {
  applicantType: "LEGAL_ENTITY",
  organizationSearch: "",
  organization: "",
  shortOrganizationName: "",
  snils: "",
  okved: "",
  oktmoOkato: "",
  legalAddress: "",
  actualAddress: "",
  actualAddressMatchesLegal: false,
  postalAddressMatchesRegistration: false,
  phones: [""],
  emails: [""],
  useSeparateContractEmail: false,
  contractEmails: [""],
  postalAddress: "",
  inn: "",
  kpp: "",
  okpo: "",
  ogrn: "",
  bankName: "",
  bik: "",
  settlementAccount: "",
  correspondentAccount: "",
  signerType: "applicant",
  signerFullName: "",
  signerPosition: "",
  signerAuthority: "",
  signerAuthorityDetails: "",
  paymentByAnotherOrganization: false,
  payerApplicantType: "LEGAL_ENTITY",
  payerOrganization: "",
  payerShortOrganizationName: "",
  payerInn: "",
  payerKpp: "",
  payerOgrn: "",
  payerOkved: "",
  payerOktmoOkato: "",
  payerOkpo: "",
  payerLegalAddress: "",
  payerActualAddress: "",
  payerActualAddressMatchesLegal: false,
  payerPostalAddress: "",
  payerPostalAddressMatchesRegistration: false,
  payerPhones: [""],
  payerEmails: [""],
  payerUseSeparateContractEmail: false,
  payerContractEmails: [""],
  payerPaymentContactFullName: "",
  payerPaymentContactPosition: "",
  payerPaymentContactPhones: [""],
  payerPaymentContactEmails: [""],
  payerBankName: "",
  payerBik: "",
  payerSettlementAccount: "",
  payerCorrespondentAccount: "",
};

export const toSelectItems = (options: CreateApplicationSelectOption[]) =>
  options.map((option) => ({
    value: option.value,
    label: option.label,
  }));

export const getCreateApplicationDocuments = (category: CreateApplicationCategory | null, context?: CreateApplicationDocumentsContext | null) => {
  if (!category) {
    return [];
  }

  const documents = CREATE_APPLICATION_DOCUMENTS_BY_CATEGORY[category];

  if (!context) {
    return documents;
  }

  return documents
    .filter((document) => document.isVisible?.(context) ?? true)
    .map((document, index) => ({
      ...toCreateApplicationDocumentMeta(document),
      order: index + 1,
      required: document.required,
    }));
};

export const buildCreateApplicationDocumentsContext = ({
  category,
  applicantData,
  adjacentObjectData,
  crossingObjectData,
}: {
  category: CreateApplicationCategory | null;
  applicantData: ApplicantDataFormValues;
  adjacentObjectData: AdjacentObjectDataFormValues;
  crossingObjectData: CrossingObjectDataFormValues;
}): CreateApplicationDocumentsContext => ({
  applicantType: applicantData.applicantType,
  signerType: applicantData.signerType,
  plannedWorkType: category === "crossing" ? (crossingObjectData.communications[0]?.plannedWorkType ?? "") : adjacentObjectData.plannedWorkType,
  connectionType: adjacentObjectData.connectionType,
  guaranteeLettersRequired: adjacentObjectData.guaranteeLettersRequired,
  hasDesignDocumentationExpertise: adjacentObjectData.hasDesignDocumentationExpertise,
});

export const buildCreateApplicationDocumentTableRows = (
  category: CreateApplicationCategory | null,
  uploads: Record<string, CreateApplicationDocumentUpload[]>,
  serverFiles: Record<string, CreateApplicationDocumentServerFile[]>,
  showValidationErrors: boolean,
  context?: CreateApplicationDocumentsContext | null,
): CreateApplicationDocumentTableRow[] =>
  getCreateApplicationDocuments(category, context).map((document) => {
    const pendingFiles = (uploads[document.id] ?? []).map((upload) => ({
      key: upload.localId,
      fileName: upload.fileName,
    }));
    const committedFiles = (serverFiles[document.id] ?? []).map((file) => ({
      key: file.fileId,
      fileName: file.fileName,
    }));
    const uploadedFiles = [...committedFiles, ...pendingFiles];

    return {
      ...document,
      uploadedFiles,
      showValidationError: showValidationErrors && document.required && uploadedFiles.length === 0,
    };
  });

export const SNACK_TITLES_SAVE_DRAFT = {
  pending: "Сохранение черновика",
  fulfilled: "Черновик сохранён",
  rejected: "Не удалось сохранить черновик",
};

export const SNACK_TITLES_DELETE_DRAFT = {
  pending: "Удаление черновика",
  fulfilled: "Черновик удалён",
  rejected: "Не удалось удалить черновик",
};

export const SNACK_TITLES_SUBMIT_APPLICATION = {
  pending: "Создание заявки",
  fulfilled: "Заявка создана",
  rejected: "Не удалось создать заявку",
};

export const getCreateApplicationStepIndex = (step: CreateApplicationStepKey) => CREATE_APPLICATION_STEPS.findIndex((item) => item.key === step);

const INVALID_CREATE_APPLICATION_FIELD_SELECTOR = [
  ".input-error",
  ".textarea-error",
  "[data-create-application-invalid='true']",
  "p.text-system-error-50",
].join(", ");

/** Scrolls the create-application page content back to the top. */
export const scrollCreateApplicationPageToTop = () => {
  const container = document.querySelector<HTMLElement>("[data-create-application-scroll-root]");

  if (container) {
    container.scrollTop = 0;
  }

  window.scrollTo(0, 0);
};

/** Scrolls to the first invalid control in document order after validation errors paint. */
export const scrollToFirstInvalidCreateApplicationField = () => {
  const run = () => {
    const errorElement = document.querySelector<HTMLElement>(INVALID_CREATE_APPLICATION_FIELD_SELECTOR);

    if (!errorElement) {
      return;
    }

    const scrollTarget = errorElement.matches(".input-error, .textarea-error, [data-create-application-invalid='true']")
      ? errorElement
      : ((errorElement.previousElementSibling as HTMLElement | null) ?? errorElement);

    scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });

    const focusTarget = scrollTarget.matches("input, textarea, select, button")
      ? scrollTarget
      : scrollTarget.querySelector<HTMLElement>("input, textarea, select, button");

    focusTarget?.focus({ preventScroll: true });
  };

  // ErrorContainer / Select apply error classes in effects — wait for paint.
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
};

export const getLastCreateApplicationStepKey = (): CreateApplicationStepKey => CREATE_APPLICATION_STEPS[CREATE_APPLICATION_STEPS.length - 1]!.key;

export const isLastCreateApplicationStep = (step: CreateApplicationStepKey) =>
  getCreateApplicationStepIndex(step) === CREATE_APPLICATION_STEPS.length - 1;

export const getCreateApplicationRequestType = (_category: CreateApplicationCategory): CreateRequestBodyTypeEnumKey => "TU_ISSUE";

export const getCrossingIntersections = (crossingObjectData: CrossingObjectDataFormValues | null | undefined): CrossingIntersectionFormValues[] =>
  crossingObjectData?.communications?.flatMap((communication) => communication.intersections ?? []) ?? [];

export const getCrossingDisplayStationSection = (crossingObjectData: CrossingObjectDataFormValues | null | undefined): string => {
  const firstIntersection = getCrossingIntersections(crossingObjectData).find((intersection) => intersection.stationSection.trim());

  return firstIntersection?.stationSection.trim() ?? "";
};

const mapCategoryToRequestSubject = (category: CreateApplicationCategory): CreateRequestBodyRequestSubjectEnumKey =>
  category === "adjacent" ? "ADJOINING" : "CROSSING";

const toOptionalApiCode = <T extends string>(value: string): T | undefined => {
  const trimmed = value.trim();

  return trimmed ? (trimmed as T) : undefined;
};

export const getApplicantTypeLabel = (value: string | undefined): string => {
  if (!value?.trim()) {
    return "—";
  }

  return APPLICANT_TYPE_LABELS[value as ApplicantType] ?? value;
};

/** Top-level form fields on external request responses. */
export type ExternalRequestFormFields = {
  adjoiningType?: CreateRequestBodyAdjoiningTypeEnumKey;
  applicantAddress?: string | null;
  applicantEmail?: string | null;
  applicantInn?: string | null;
  applicantKpp?: string | null;
  applicantName?: string | null;
  applicantOkpo?: string | null;
  applicantPhone?: string | null;
  applicantType?: CreateRequestBodyApplicantTypeEnumKey;
  plannedWorksType?: CreateRequestBodyPlannedWorksTypeEnumKey;
  railway?: CreateRequestBodyRailwayEnumKey;
  requestSubject?: CreateRequestBodyRequestSubjectEnumKey;
  trackFunding?: CreateRequestBodyTrackFundingEnumKey;
  section?: string | null;
};

export const getExternalRequestFormFields = (application: RequestDetailResponse): ExternalRequestFormFields =>
  application as RequestDetailResponse & ExternalRequestFormFields;

const mapApplicantTypeToApi = (value: ApplicantType): CreateRequestBodyApplicantTypeEnumKey => value;

const mapApiApplicantTypeToForm = (value: CreateRequestBodyApplicantTypeEnumKey | null | undefined): ApplicantType | undefined => value ?? undefined;

const getCreateApplicationSection = (
  category: CreateApplicationCategory,
  adjacentObjectData: AdjacentObjectDataFormValues,
  crossingObjectData: CrossingObjectDataFormValues,
): string => (category === "crossing" ? getCrossingDisplayStationSection(crossingObjectData) : adjacentObjectData.stationSection.trim());

const getCreateApplicationPlannedWorksType = (
  category: CreateApplicationCategory,
  adjacentObjectData: AdjacentObjectDataFormValues,
  crossingObjectData: CrossingObjectDataFormValues,
): CreateRequestBodyPlannedWorksTypeEnumKey | undefined =>
  category === "crossing"
    ? toOptionalApiCode(crossingObjectData.communications[0]?.plannedWorkType ?? "")
    : toOptionalApiCode(adjacentObjectData.plannedWorkType);

const applyApiFieldsToAdjacentObjectData = (data: AdjacentObjectDataFormValues, draft: RequestDetailResponse): AdjacentObjectDataFormValues => {
  const apiFields = getExternalRequestFormFields(draft);

  return {
    ...data,
    railway: data.railway || apiFields.railway || "",
    stationSection: data.stationSection || apiFields.section?.trim() || "",
    plannedWorkType: data.plannedWorkType || apiFields.plannedWorksType || "",
    connectionType: data.connectionType || apiFields.adjoiningType || "",
    fundingSource: data.fundingSource || draft.trackFunding || "",
  };
};

const applyApiFieldsToCrossingObjectData = (data: CrossingObjectDataFormValues, draft: RequestDetailResponse): CrossingObjectDataFormValues => {
  const apiFields = getExternalRequestFormFields(draft);
  const railway = data.railway || apiFields.railway || "";
  const section = getCrossingDisplayStationSection(data) || apiFields.section?.trim() || "";
  const plannedWorkType = apiFields.plannedWorksType || "";

  const hasDisplayStationSection = Boolean(getCrossingDisplayStationSection(data));

  const communications = data.communications.map((communication, communicationIndex) => {
    let nextCommunication = communication;

    if (communicationIndex === 0 && plannedWorkType && !communication.plannedWorkType) {
      nextCommunication = { ...nextCommunication, plannedWorkType };
    }

    if (communicationIndex === 0 && section && !hasDisplayStationSection) {
      nextCommunication = {
        ...nextCommunication,
        intersections: (nextCommunication.intersections ?? [createDefaultCrossingIntersection()]).map((item, index) =>
          index === 0 ? { ...item, stationSection: section } : item,
        ),
      };
    }

    return nextCommunication;
  });

  return {
    ...data,
    railway,
    communications,
  };
};

const applyApiFieldsToApplicantData = (data: ApplicantDataFormValues, draft: RequestDetailResponse): ApplicantDataFormValues => {
  const apiFields = getExternalRequestFormFields(draft);
  const applicantType = mapApiApplicantTypeToForm(apiFields.applicantType) ?? data.applicantType;

  return {
    ...data,
    applicantType,
    organization: data.organization || apiFields.applicantName?.trim() || "",
    legalAddress: data.legalAddress || apiFields.applicantAddress?.trim() || "",
    inn: data.inn || apiFields.applicantInn?.trim() || "",
    kpp: data.kpp || apiFields.applicantKpp?.trim() || "",
    okpo: data.okpo || apiFields.applicantOkpo?.trim() || "",
    phones: data.phones[0]?.trim() ? data.phones : apiFields.applicantPhone?.trim() ? [apiFields.applicantPhone] : data.phones,
    emails: data.emails[0]?.trim() ? data.emails : apiFields.applicantEmail?.trim() ? [apiFields.applicantEmail] : data.emails,
  };
};

export type BuildCreateApplicationDraftRequestDataParams = {
  category: CreateApplicationCategory;
  title: string;
  type: CreateRequestBodyTypeEnumKey;
  additionalData: string;
  adjacentObjectData: AdjacentObjectDataFormValues;
  crossingObjectData: CrossingObjectDataFormValues;
  applicantData: ApplicantDataFormValues;
};

export const buildCreateApplicationDraftRequestData = ({
  category,
  title,
  type,
  additionalData,
  adjacentObjectData,
  crossingObjectData,
  applicantData,
}: BuildCreateApplicationDraftRequestDataParams): UpdateDraftData => {
  const objectDescription =
    category === "crossing"
      ? { category, ...crossingObjectData }
      : {
          category,
          ...adjacentObjectData,
          // Бэкенд принимает cadastralNumbers только как массив строк; пустые значения не отправляем.
          cadastralNumbers: adjacentObjectData.cadastralNumbers.map((item) => item.trim()).filter(Boolean),
        };
  const railwaySource = category === "crossing" ? crossingObjectData.railway : adjacentObjectData.railway;
  const section = getCreateApplicationSection(category, adjacentObjectData, crossingObjectData);

  return {
    title,
    type,
    additionalData,
    objectDescription,
    requestSubject: mapCategoryToRequestSubject(category),
    railway: toOptionalApiCode(railwaySource),
    section: section || undefined,
    plannedWorksType: getCreateApplicationPlannedWorksType(category, adjacentObjectData, crossingObjectData),
    adjoiningType: category === "adjacent" ? toOptionalApiCode(adjacentObjectData.connectionType) : undefined,
    trackFunding: category === "adjacent" ? toOptionalApiCode(adjacentObjectData.fundingSource) : undefined,
    applicantType: mapApplicantTypeToApi(applicantData.applicantType),
    applicantName: applicantData.organization.trim() || undefined,
    applicantAddress: applicantData.legalAddress.trim() || undefined,
    applicantEmail: applicantData.emails[0]?.trim() || undefined,
    applicantInn: applicantData.inn.trim() || undefined,
    applicantKpp: applicantData.kpp.trim() || undefined,
    applicantOkpo: applicantData.okpo.trim() || undefined,
    applicantPhone: applicantData.phones[0]?.trim() || undefined,
  };
};

export const buildCreateApplicationDraftTitle = (categoryLabel: string) => `Новая заявка — ${categoryLabel}`;

export const buildCreateApplicationAdditionalData = (meta: CreateApplicationDraftMeta) => JSON.stringify(meta);

export const parseCreateApplicationAdditionalData = (value?: string | null): CreateApplicationDraftMeta | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<CreateApplicationDraftMeta>;

    if (parsed.category === "adjacent" || parsed.category === "crossing") {
      return {
        category: parsed.category,
        categoryLabel: parsed.categoryLabel ?? "",
        draftTitle: parsed.draftTitle,
        applicantData: parsed.applicantData,
        documents: parsed.documents,
      };
    }
  } catch {
    return null;
  }

  return null;
};

export const isValidCreateApplicationDraftId = (id: string | null | undefined): id is string => z.uuid().safeParse(id).success;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const mergeCrossingIntersectionItem = (
  defaults: CrossingIntersectionFormValues,
  source: Record<string, unknown> | undefined,
): CrossingIntersectionFormValues => {
  if (!source) {
    return { ...defaults };
  }

  return {
    stationSection: typeof source.stationSection === "string" ? source.stationSection : defaults.stationSection,
    junctionLocation: typeof source.junctionLocation === "string" ? source.junctionLocation : defaults.junctionLocation,
    coordinates: typeof source.coordinates === "string" ? source.coordinates : defaults.coordinates,
    comment: typeof source.comment === "string" ? source.comment : defaults.comment,
  };
};

const mergeCrossingCommunicationItem = (
  defaults: CrossingCommunicationFormValues,
  source: Record<string, unknown> | undefined,
  fallbackIntersections?: CrossingIntersectionFormValues[],
): CrossingCommunicationFormValues => {
  const defaultIntersections = fallbackIntersections?.length ? fallbackIntersections : defaults.intersections.map((item) => ({ ...item }));

  if (!source) {
    return {
      ...defaults,
      intersections: defaultIntersections,
    };
  }

  const nestedIntersections = Array.isArray(source.intersections)
    ? source.intersections.map((item) => mergeCrossingIntersectionItem(DEFAULT_CROSSING_INTERSECTION, isRecord(item) ? item : undefined))
    : [];

  return {
    utilityStructureType: typeof source.utilityStructureType === "string" ? source.utilityStructureType : defaults.utilityStructureType,
    utilityPurpose: typeof source.utilityPurpose === "string" ? source.utilityPurpose : defaults.utilityPurpose,
    plannedWorkType: typeof source.plannedWorkType === "string" ? source.plannedWorkType : defaults.plannedWorkType,
    crossingTechnology: typeof source.crossingTechnology === "string" ? source.crossingTechnology : defaults.crossingTechnology,
    technicalCharacteristics:
      typeof source.technicalCharacteristics === "string" ? source.technicalCharacteristics : defaults.technicalCharacteristics,
    intersections: nestedIntersections.length > 0 ? nestedIntersections : defaultIntersections,
  };
};

export const mergeCrossingObjectDescriptionFormValues = (
  defaults: CrossingObjectDataFormValues,
  source: Record<string, unknown> | null | undefined,
): CrossingObjectDataFormValues => {
  if (!source) {
    return defaults;
  }

  const { category: _category, ...rest } = source;
  const legacyIntersections = Array.isArray(rest.intersections)
    ? rest.intersections.map((item) => mergeCrossingIntersectionItem(DEFAULT_CROSSING_INTERSECTION, isRecord(item) ? item : undefined))
    : [];

  return {
    projectName: typeof rest.projectName === "string" ? rest.projectName : defaults.projectName,
    railway: typeof rest.railway === "string" ? rest.railway : defaults.railway,
    communications: Array.isArray(rest.communications)
      ? rest.communications.map((item, index) =>
          mergeCrossingCommunicationItem(
            createDefaultCrossingCommunication(),
            isRecord(item) ? item : undefined,
            index === 0 && legacyIntersections.length > 0 ? legacyIntersections : undefined,
          ),
        )
      : defaults.communications.map((communication) => ({
          ...communication,
          intersections: communication.intersections.map((item) => ({ ...item })),
        })),
    implementationStartDate: typeof rest.implementationStartDate === "string" ? rest.implementationStartDate : defaults.implementationStartDate,
    implementationEndDate: typeof rest.implementationEndDate === "string" ? rest.implementationEndDate : defaults.implementationEndDate,
    commissioningDate: typeof rest.commissioningDate === "string" ? rest.commissioningDate : defaults.commissioningDate,
  };
};

export const mergeObjectDescriptionFormValues = <T extends AdjacentObjectDataFormValues>(
  defaults: T,
  source: Record<string, unknown> | null | undefined,
): T => {
  if (!source) {
    return defaults;
  }

  const { category: _category, ...rest } = source;
  const result = { ...defaults } as Record<string, unknown>;

  for (const key of Object.keys(defaults)) {
    const value = rest[key];

    if (key === "cadastralNumbers") {
      result[key] = parseAdjacentCadastralNumbers(value);
      continue;
    }

    if (typeof value === "string") {
      result[key] = value;
    }

    if (typeof value === "boolean") {
      result[key] = value;
    }
  }

  return result as T;
};

/**
 * Приводит `cadastralNumbers` из objectDescription (произвольный JSON) к массиву строк.
 * Поддерживает и новый формат (массив строк), и legacy-черновики, где поле хранилось строкой.
 */
const parseAdjacentCadastralNumbers = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }

  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }

  return [];
};

export const mergeApplicantDataFormValues = (source: Partial<ApplicantDataFormValues> | undefined): ApplicantDataFormValues => {
  const contractEmails = source?.contractEmails?.length
    ? source.contractEmails
    : source?.useSeparateContractEmail && source.emails?.length
      ? source.emails.slice(1)
      : DEFAULT_APPLICANT_DATA.contractEmails;
  const payerContractEmails = source?.payerContractEmails?.length
    ? source.payerContractEmails
    : source?.payerUseSeparateContractEmail && source.payerEmails?.length
      ? source.payerEmails.slice(1)
      : DEFAULT_APPLICANT_DATA.payerContractEmails;

  return {
    ...DEFAULT_APPLICANT_DATA,
    ...source,
    applicantType: source?.applicantType ?? DEFAULT_APPLICANT_DATA.applicantType,
    signerType: source?.signerType ?? DEFAULT_APPLICANT_DATA.signerType,
    payerApplicantType: source?.payerApplicantType ?? DEFAULT_APPLICANT_DATA.payerApplicantType,
    phones: source?.phones?.length ? source.phones : DEFAULT_APPLICANT_DATA.phones,
    emails: source?.emails?.length ? source.emails : DEFAULT_APPLICANT_DATA.emails,
    contractEmails: contractEmails.length ? contractEmails : DEFAULT_APPLICANT_DATA.contractEmails,
    payerPhones: source?.payerPhones?.length ? source.payerPhones : DEFAULT_APPLICANT_DATA.payerPhones,
    payerEmails: source?.payerEmails?.length ? source.payerEmails : DEFAULT_APPLICANT_DATA.payerEmails,
    payerContractEmails: payerContractEmails.length ? payerContractEmails : DEFAULT_APPLICANT_DATA.payerContractEmails,
    payerPaymentContactPhones: source?.payerPaymentContactPhones?.length
      ? source.payerPaymentContactPhones
      : DEFAULT_APPLICANT_DATA.payerPaymentContactPhones,
    payerPaymentContactEmails: source?.payerPaymentContactEmails?.length
      ? source.payerPaymentContactEmails
      : DEFAULT_APPLICANT_DATA.payerPaymentContactEmails,
  };
};

export const parseApplicantDataFromAdditionalData = (additionalData?: string | null): ApplicantDataFormValues | null => {
  const meta = parseCreateApplicationAdditionalData(additionalData);

  return meta?.applicantData ? mergeApplicantDataFormValues(meta.applicantData) : null;
};

export const parseAdjacentObjectDataFromObjectDescription = (
  objectDescription?: Record<string, unknown> | null,
): AdjacentObjectDataFormValues | null => {
  if (!objectDescription || typeof objectDescription !== "object") {
    return null;
  }

  if (objectDescription.category !== "adjacent") {
    return null;
  }

  return mergeObjectDescriptionFormValues(DEFAULT_ADJACENT_OBJECT_DATA, objectDescription);
};

export const parseCrossingObjectDataFromObjectDescription = (
  objectDescription?: Record<string, unknown> | null,
): CrossingObjectDataFormValues | null => {
  if (!objectDescription || typeof objectDescription !== "object") {
    return null;
  }

  if (objectDescription.category !== "crossing") {
    return null;
  }

  return mergeCrossingObjectDescriptionFormValues(DEFAULT_CROSSING_OBJECT_DATA, objectDescription);
};

const CREATE_APPLICATION_ATTACHMENT_DESCRIPTION_PREFIX = /^([^:]+):\s*/;

export const parseCreateApplicationDocumentIdFromAttachmentDescription = (description?: string | null): string | null => {
  if (!description?.trim()) {
    return null;
  }

  const match = description.match(CREATE_APPLICATION_ATTACHMENT_DESCRIPTION_PREFIX);

  return match?.[1]?.trim() || null;
};

export const formatCreateApplicationFileMetaDisplayName = (fileMeta: { baseName: string; ext?: string | null }): string =>
  fileMeta.ext ? `${fileMeta.baseName}.${fileMeta.ext}` : fileMeta.baseName;

type BuildCreateApplicationDocumentServerFilesParams = {
  attachments?: Array<{ description?: string | null; fileId?: string | null }>;
  fileMetasById?: Record<string, { baseName: string; ext?: string | null }>;
};

export const buildCreateApplicationDocumentServerFiles = ({
  attachments = [],
  fileMetasById = {},
}: BuildCreateApplicationDocumentServerFilesParams): Record<string, CreateApplicationDocumentServerFile[]> => {
  const result: Record<string, CreateApplicationDocumentServerFile[]> = {};

  for (const attachment of attachments) {
    const documentId = parseCreateApplicationDocumentIdFromAttachmentDescription(attachment.description);
    const fileId = attachment.fileId;

    if (!documentId || !fileId) {
      continue;
    }

    const fileMeta = fileMetasById[fileId];

    if (!fileMeta) {
      continue;
    }

    const nextFile: CreateApplicationDocumentServerFile = {
      fileId,
      fileName: formatCreateApplicationFileMetaDisplayName(fileMeta),
    };
    const existing = result[documentId] ?? [];

    if (existing.some((file) => file.fileId === fileId)) {
      continue;
    }

    result[documentId] = [...existing, nextFile];
  }

  return result;
};

type ApplyCreateApplicationDraftAttachmentContext = {
  attachments: Array<{ description?: string | null; fileId?: string | null }>;
  fileMetasById: Record<string, { baseName: string; ext?: string | null }>;
};

export const applyCreateApplicationDraftToPageStore = (
  store: PageStore,
  draft: RequestDetailResponse,
  attachmentContext?: ApplyCreateApplicationDraftAttachmentContext,
) => {
  if (!isEditableExternalApplicationStatus(draft.status)) {
    return;
  }

  const meta = parseCreateApplicationAdditionalData(draft.additionalData);

  if (!meta?.category) {
    return;
  }

  const objectDescription =
    draft.objectDescription && typeof draft.objectDescription === "object" ? (draft.objectDescription as Record<string, unknown>) : null;

  const adjacentObjectData =
    meta.category === "adjacent"
      ? applyApiFieldsToAdjacentObjectData(mergeObjectDescriptionFormValues(DEFAULT_ADJACENT_OBJECT_DATA, objectDescription), draft)
      : undefined;
  const crossingObjectData =
    meta.category === "crossing"
      ? applyApiFieldsToCrossingObjectData(mergeCrossingObjectDescriptionFormValues(DEFAULT_CROSSING_OBJECT_DATA, objectDescription), draft)
      : undefined;
  const applicantData = meta.applicantData ? applyApiFieldsToApplicantData(mergeApplicantDataFormValues(meta.applicantData), draft) : undefined;

  store.hydrateFromDraft({
    category: meta.category,
    adjacentObjectData,
    crossingObjectData,
    applicantData,
    documentServerFiles: buildCreateApplicationDocumentServerFiles({
      attachments: attachmentContext?.attachments,
      fileMetasById: attachmentContext?.fileMetasById,
    }),
    editingMode: draft.status === "REVISION_REQUIRED" ? "revision" : "draft",
  });
};

// ---------------------------------- STORE ----------------------------------

/**
 * Глубокое сравнение значений форм. Ключ со значением `undefined` считается равным
 * отсутствующему ключу (react-hook-form может опускать пустые поля).
 */
const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }

  if (a === null || b === null || a === undefined || b === undefined) {
    return a === b;
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }

    return a.every((item, index) => isDeepEqual(item, b[index]));
  }

  if (typeof a === "object" && typeof b === "object") {
    const recordA = a as Record<string, unknown>;
    const recordB = b as Record<string, unknown>;

    for (const key of new Set([...Object.keys(recordA), ...Object.keys(recordB)])) {
      if (!isDeepEqual(recordA[key], recordB[key])) {
        return false;
      }
    }

    return true;
  }

  return false;
};

type CreateApplicationEditingMode = "draft" | "revision";

type HydrateCreateApplicationDraftParams = {
  category: CreateApplicationCategory;
  adjacentObjectData?: AdjacentObjectDataFormValues;
  crossingObjectData?: CrossingObjectDataFormValues;
  applicantData?: ApplicantDataFormValues;
  documentServerFiles?: Record<string, CreateApplicationDocumentServerFile[]>;
  editingMode?: CreateApplicationEditingMode;
};

class PageStore {
  public activeStep: CreateApplicationStepKey = "type";
  public selectedCategory: CreateApplicationCategory | null = DEFAULT_CREATE_APPLICATION_CATEGORY;
  public editingMode: CreateApplicationEditingMode | null = null;
  /**
   * Признак «черновик изменён»: сбрасывается при гидратации черновика и после
   * успешного сохранения. Управляет доступностью кнопки «Сохранить изменения».
   */
  public isDraftDirty = false;
  public documentServerFiles: Record<string, CreateApplicationDocumentServerFile[]> = {};
  public pendingAttachmentFileIdsToDelete = new Set<string>();
  public adjacentObjectData: AdjacentObjectDataFormValues = { ...DEFAULT_ADJACENT_OBJECT_DATA };
  public crossingObjectData: CrossingObjectDataFormValues = createDefaultCrossingObjectData();
  public isObjectDataStepValid = false;
  public objectDataValidator: (() => Promise<boolean>) | null = null;
  public documentUploads: Record<string, CreateApplicationDocumentUpload[]> = {};
  public isDocumentsStepValid = false;
  public documentsValidationAttempt = false;
  public applicantData: ApplicantDataFormValues = { ...DEFAULT_APPLICANT_DATA };
  public isApplicantStepValid = false;
  public applicantDataValidator: (() => Promise<boolean>) | null = null;

  constructor() {
    makeAutoObservable(this, {
      objectDataValidator: false,
      applicantDataValidator: false,
    });
  }

  public setSelectedCategory(category: CreateApplicationCategory) {
    if (category !== this.selectedCategory) {
      this.isDraftDirty = true;
    }

    this.selectedCategory = category;
    this.editingMode = null;
    this.documentUploads = {};
    this.documentServerFiles = {};
    this.pendingAttachmentFileIdsToDelete.clear();
    this.documentsValidationAttempt = false;
    this.syncDocumentsStepValid();
  }

  public hydrateFromDraft({
    category,
    adjacentObjectData,
    crossingObjectData,
    applicantData,
    documentServerFiles = {},
    editingMode = "draft",
  }: HydrateCreateApplicationDraftParams) {
    this.selectedCategory = category;
    this.editingMode = editingMode;
    this.isDraftDirty = false;
    this.documentUploads = {};
    this.documentServerFiles = documentServerFiles;
    this.pendingAttachmentFileIdsToDelete.clear();
    this.documentsValidationAttempt = false;

    if (adjacentObjectData) {
      this.adjacentObjectData = adjacentObjectData;
    }

    if (crossingObjectData) {
      this.crossingObjectData = crossingObjectData;
    }

    if (applicantData) {
      this.applicantData = applicantData;
    }

    this.syncDocumentsStepValid();
  }

  /** Сброс признака «черновик изменён» после успешного сохранения. */
  public markDraftSaved() {
    this.isDraftDirty = false;
  }

  public setAdjacentObjectData(data: AdjacentObjectDataFormValues) {
    if (!isDeepEqual(data, this.adjacentObjectData)) {
      this.isDraftDirty = true;
    }

    this.adjacentObjectData = data;
    this.syncDocumentsStepValid();
  }

  public setCrossingObjectData(data: CrossingObjectDataFormValues) {
    if (!isDeepEqual(data, this.crossingObjectData)) {
      this.isDraftDirty = true;
    }

    this.crossingObjectData = data;
    this.syncDocumentsStepValid();
  }

  public getObjectDataProjectName() {
    if (this.selectedCategory === "crossing") {
      return this.crossingObjectData.projectName.trim();
    }

    return this.adjacentObjectData.projectName.trim();
  }

  public setObjectDataStepValid(isValid: boolean) {
    this.isObjectDataStepValid = isValid;
  }

  public setObjectDataValidator(validator: (() => Promise<boolean>) | null) {
    this.objectDataValidator = validator;
  }

  public validateObjectDataStep() {
    if (!this.objectDataValidator) {
      return Promise.resolve(true);
    }

    return this.objectDataValidator();
  }

  private queueServerFileDeletion(fileId: string) {
    this.pendingAttachmentFileIdsToDelete.add(fileId);
  }

  public getPendingAttachmentFileIdsToDelete(): string[] {
    return [...this.pendingAttachmentFileIdsToDelete];
  }

  public clearPendingAttachmentDeletions(fileIds: string[]) {
    for (const fileId of fileIds) {
      this.pendingAttachmentFileIdsToDelete.delete(fileId);
    }
  }

  public commitDocumentUpload(documentId: string, localId: string, fileId: string, fileName: string) {
    const pendingUploads = this.documentUploads[documentId] ?? [];
    const nextPendingUploads = pendingUploads.filter((upload) => upload.localId !== localId);
    const nextUploads = { ...this.documentUploads };

    if (nextPendingUploads.length > 0) {
      nextUploads[documentId] = nextPendingUploads;
    } else {
      delete nextUploads[documentId];
    }

    const existingServerFiles = this.documentServerFiles[documentId] ?? [];
    const withoutDuplicate = existingServerFiles.filter((file) => file.fileId !== fileId);

    this.documentUploads = nextUploads;
    this.documentServerFiles = {
      ...this.documentServerFiles,
      [documentId]: [...withoutDuplicate, { fileId, fileName }],
    };
    this.syncDocumentsStepValid();
  }

  public addDocumentUploads(documentId: string, files: File[]) {
    if (files.length === 0) {
      return;
    }

    this.isDraftDirty = true;

    const existingUploads = this.documentUploads[documentId] ?? [];
    const nextUploads = files.map((file) => ({
      localId: createCreateApplicationDocumentUploadLocalId(),
      file,
      fileName: file.name,
    }));

    this.documentUploads = {
      ...this.documentUploads,
      [documentId]: [...existingUploads, ...nextUploads],
    };
    this.syncDocumentsStepValid();
  }

  public removeDocumentFile(documentId: string, fileKey: string) {
    this.isDraftDirty = true;

    const pendingUploads = this.documentUploads[documentId] ?? [];
    const pendingUpload = pendingUploads.find((upload) => upload.localId === fileKey);

    if (pendingUpload) {
      const nextPendingUploads = pendingUploads.filter((upload) => upload.localId !== fileKey);
      const nextUploads = { ...this.documentUploads };

      if (nextPendingUploads.length > 0) {
        nextUploads[documentId] = nextPendingUploads;
      } else {
        delete nextUploads[documentId];
      }

      this.documentUploads = nextUploads;
      this.syncDocumentsStepValid();
      return;
    }

    const serverFiles = this.documentServerFiles[documentId] ?? [];
    const serverFile = serverFiles.find((file) => file.fileId === fileKey);

    if (!serverFile) {
      return;
    }

    this.queueServerFileDeletion(serverFile.fileId);

    const nextServerFilesList = serverFiles.filter((file) => file.fileId !== fileKey);
    const nextServerFiles = { ...this.documentServerFiles };

    if (nextServerFilesList.length > 0) {
      nextServerFiles[documentId] = nextServerFilesList;
    } else {
      delete nextServerFiles[documentId];
    }

    this.documentServerFiles = nextServerFiles;
    this.syncDocumentsStepValid();
  }

  public getDocumentsContext(): CreateApplicationDocumentsContext {
    return buildCreateApplicationDocumentsContext({
      category: this.selectedCategory,
      applicantData: this.applicantData,
      adjacentObjectData: this.adjacentObjectData,
      crossingObjectData: this.crossingObjectData,
    });
  }

  public syncDocumentsStepValid() {
    const requiredDocuments = getCreateApplicationDocuments(this.selectedCategory, this.getDocumentsContext()).filter(
      (document) => document.required,
    );
    this.isDocumentsStepValid = requiredDocuments.every(
      (document) => (this.documentUploads[document.id]?.length ?? 0) > 0 || (this.documentServerFiles[document.id]?.length ?? 0) > 0,
    );
  }

  public validateDocumentsStep() {
    this.syncDocumentsStepValid();

    if (this.isDocumentsStepValid) {
      this.documentsValidationAttempt = false;
      return true;
    }

    this.documentsValidationAttempt = true;
    return false;
  }

  public resetDocumentsValidationAttempt() {
    this.documentsValidationAttempt = false;
  }

  public setApplicantData(data: ApplicantDataFormValues) {
    if (!isDeepEqual(data, this.applicantData)) {
      this.isDraftDirty = true;
    }

    this.applicantData = data;
    this.syncDocumentsStepValid();
  }

  public setApplicantStepValid(isValid: boolean) {
    this.isApplicantStepValid = isValid;
  }

  public setApplicantDataValidator(validator: (() => Promise<boolean>) | null) {
    this.applicantDataValidator = validator;
  }

  public validateApplicantStep() {
    if (!this.applicantDataValidator) {
      return Promise.resolve(true);
    }

    return this.applicantDataValidator();
  }

  public setActiveStep(step: CreateApplicationStepKey) {
    this.activeStep = step;
  }

  public goToNextStep() {
    const currentIndex = getCreateApplicationStepIndex(this.activeStep);
    const nextStep = CREATE_APPLICATION_STEPS[currentIndex + 1];

    if (nextStep) {
      this.activeStep = nextStep.key;
    }
  }

  public goToPreviousStep() {
    const currentIndex = getCreateApplicationStepIndex(this.activeStep);
    const previousStep = CREATE_APPLICATION_STEPS[currentIndex - 1];

    if (previousStep) {
      this.resetDocumentsValidationAttempt();
      this.activeStep = previousStep.key;
    }
  }
}

const { StoreProvider: PageStoreProvider, useStore: usePageStore } = makeStoreProvider(PageStore);

export { PageStoreProvider, usePageStore };
